import React from 'react';
import { useGetDomNode } from '../../hooks/useGetDomNode';
import { useAppear } from '../../hooks/useAppear';
import { AppUserInterface } from '../AppUserInterface';
import { BlockInfoType } from '../../lib/REST/types';
import { Portal } from '../Portal';
import { DefaultContentTracker } from '../DefaultContentTracker';
import { Script } from '../AppUserInterface/Script';

type BlockReplacerCommonType = {
	forceSetInterval?: boolean;
	getComment?: (prevHTML: string) => string;
	onAppear?: () => void;
	onRendered?: () => void;
	onLinkClick?: (url: string) => Promise<unknown>;
};

type BlockReplacerPropsType = BlockReplacerCommonType & BlockInfoType;

export function BlockReplacer({
	component,
	selector,
	forceSetInterval = false,
	getComment = () => '',
	onAppear = () => {},
	onRendered = () => {},
	onLinkClick = () => Promise.resolve(null),
	customJs,
}: BlockReplacerPropsType) {
	const isContentToReplace = !!component;

	if (!selector) {
		const head = document.head || document.getElementsByTagName('head')[0];
		// NOTE: Typescript wrongly merges two types so doesn't understand that !selector => customJs !== undefined
		return (
			<Script
				url={customJs as string}
				container={head}
				onRendered={onRendered}
			/>
		);
	}

	const portalContainer = useGetDomNode({
		selector,
		forceSetInterval,
	});

	useAppear(portalContainer, onAppear);

	if (!portalContainer) {
		return null;
	}

	if (!isContentToReplace) {
		return (
			<DefaultContentTracker
				portalContainer={portalContainer}
				onRendered={onRendered}
				onLinkClick={onLinkClick}
			/>
		);
	}

	return (
		<Portal
			selector={selector}
			portalContainer={portalContainer}
			component={
				<AppUserInterface
					component={component}
					onRendered={onRendered}
					onLinkClick={onLinkClick}
					customJs={customJs}
					customJsContainer={portalContainer}
				/>
			}
			getCommentMessage={getComment}
		/>
	);
}
